<div class="users-view">

  @if (successMessage) {
    <div class="alert alert-success">
      <i class="fas fa-check-circle"></i> {{ successMessage }}
    </div>
  }

  <div class="header-row">
    <div>
      <h2>Users</h2>
      <p>Create users, manage roles, update contact details, and review account status.</p>
    </div>
    <div class="toolbar-actions">
      <button type="button" class="btn btn-secondary" (click)="loadUsers()" [disabled]="loading">
        <i class="fas fa-sync-alt"></i> Refresh
      </button>
      @if (canCreateUser) {
        <button type="button" class="btn btn-primary" (click)="openCreateModal()">
          <i class="fas fa-user-plus"></i> New User
        </button>
      }
    </div>
  </div>

  @if (errorMessage) {
    <div class="alert alert-danger">
      <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
    </div>
  }

  <div class="card filters-card">
    <div class="card-body">
      <div class="filters-row">
        <div>
          <label>Search</label>
          <input type="text" class="form-control" [(ngModel)]="searchQuery"
            (ngModelChange)="onFiltersChanged()" placeholder="Name, username, email, or ID number...">
        </div>
        <div>
          <label>Status</label>
          <select class="form-control" [(ngModel)]="statusFilter" (ngModelChange)="onFiltersChanged()">
            <option value="">All statuses</option>
            @for (status of statusOptions; track status) {
              <option [value]="status">{{ status | titlecase }}</option>
            }
          </select>
        </div>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card-header">All Users <span class="count-pill">{{ filteredUsers.length }}</span></div>
    <div class="card-body no-padding">
      <div class="table-wrap">
        <table class="table users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Contact</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (loading) {
              <tr>
                <td colspan="6" class="empty-cell">Loading users…</td>
              </tr>
            } @else if (filteredUsers.length === 0) {
              <tr>
                <td colspan="6" class="empty-cell">No users found.</td>
              </tr>
            } @else {
            @for (user of paginatedUsers; track user.id) {
              <tr>
                <td>
                  <div class="user-cell">
                    <strong>{{ userName(user) }}</strong>
                    <small>{{ user.userName }}</small>
                  </div>
                </td>
                <td>
                  <div class="contact-cell">
                    <div>{{ user.emailAddress }}</div>
                    <small>{{ user.phoneNumber }}</small>
                  </div>
                </td>
                <td>
                  <div class="roles-cell">
                    @if (user.roles.length) {
                      @for (role of user.roles; track role) {
                        <span class="role-chip">{{ role }}</span>
                      }
                    } @else {
                      <span class="muted">None assigned</span>
                    }
                  </div>
                </td>
                <td>
                  @if (authorization.hasPermission(permissions.UserManage)) {
                    <select class="form-control status-select" [ngModel]="user.status"
                      (ngModelChange)="updateStatus(user, $event)">
                      @for (status of statusOptions; track status) {
                        <option [ngValue]="status">{{ status | titlecase }}</option>
                      }
                    </select>
                  } @else {
                    <span class="badge" [ngClass]="getStatusClass(user.status)">{{ user.status }}</span>
                  }
                </td>
                <td>
                  @if (user.lastLoginDate) {
                    {{ user.lastLoginDate | date: 'dd MMM yyyy HH:mm' }}
                  } @else {
                    <span class="muted">Never</span>
                  }
                </td>
                <td>
                  <div class="row-actions">
                    @if (authorization.hasPermission(permissions.UserManage) && user.status !== 'DELETED') {
                      <button type="button" class="btn btn-secondary btn-sm" (click)="openContactModal(user)"
                        title="Edit contact details">
                        <i class="fas fa-address-card"></i> Contact
                      </button>
                    }
                    @if (canManageRoles && user.status !== 'DELETED') {
                      <button type="button" class="btn btn-secondary btn-sm" (click)="openRolesModal(user)"
                        title="Edit roles">
                        <i class="fas fa-user-tag"></i> Roles
                      </button>
                    }
                    @if (authorization.hasPermission(permissions.UserManage)) {
                      <button type="button" class="btn btn-secondary btn-sm" (click)="openActivityModal(user)"
                        title="View activity logs">
                        <i class="fas fa-history"></i> Activity
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
            }
          </tbody>
        </table>
      </div>

      <app-pagination [currentPage]="currentPage" [totalPages]="totalPages"
        [totalItems]="filteredUsers.length" [showingFrom]="showingFrom" [showingTo]="showingTo"
        itemLabel="users" (pageChange)="goToPage($event)"></app-pagination>
    </div>
  </div>
</div>

<!-- Create User Modal -->
@if (showCreateModal) {
  <div class="modal-overlay" (click)="closeCreateModal()">
    <div class="modal-content user-modal" role="dialog" aria-modal="true" aria-labelledby="create-user-title"
      (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title" id="create-user-title">Create New User</div>
        <button type="button" class="modal-close" (click)="closeCreateModal()">&times;</button>
      </div>
      <div class="modal-body">
        @if (errorMessage) {
          <div class="alert alert-danger">{{ errorMessage }}</div>
        }

        <div class="form-grid">
          <div>
            <label>First Name</label>
            <input type="text" class="form-control" [(ngModel)]="createForm.firstName">
          </div>
          <div>
            <label>Last Name</label>
            <input type="text" class="form-control" [(ngModel)]="createForm.lastName">
          </div>
          <div>
            <label>Email Address</label>
            <input type="email" class="form-control" [(ngModel)]="createForm.emailAddress">
          </div>
          <div>
            <label>Phone Number</label>
            <input type="text" class="form-control" [(ngModel)]="createForm.phoneNumber">
          </div>
          <div class="full-width">
            <label>Address</label>
            <input type="text" class="form-control" [(ngModel)]="createForm.address">
          </div>
          <div>
            <label>Username</label>
            <input type="text" class="form-control" [(ngModel)]="createForm.userName">
          </div>
          <div>
            <label>Password</label>
            <input type="password" class="form-control" [(ngModel)]="createForm.password">
          </div>
          <div>
            <label>Identity Type</label>
            <select class="form-control" [(ngModel)]="createForm.identityTypeId">
              <option [ngValue]="''">Select identity type</option>
              @for (type of identityTypes; track type.id) {
                <option [ngValue]="type.id.toString()">{{ type.name }}</option>
              }
            </select>
          </div>
          <div>
            <label>Identity Number</label>
            <input type="text" class="form-control" [(ngModel)]="createForm.identityNumber">
          </div>
        </div>

        <div class="role-section">
          <label>Roles</label>
          <div class="role-checkboxes">
            @for (role of roles; track role.id) {
              <label class="role-checkbox">
                <input type="checkbox" [checked]="isCreateRoleSelected(role.id)"
                  (change)="toggleCreateRole(role.id, $any($event.target).checked)">
                {{ role.name }}
              </label>
            }
          </div>
        </div>

        <div class="form-grid">
          <div>
            <label>Institution (optional)</label>
            <select class="form-control" [(ngModel)]="createForm.institutionId" (ngModelChange)="onInstitutionChange()">
              <option [ngValue]="null">None</option>
              @for (institution of institutions; track institution.id) {
                <option [ngValue]="institution.id">{{ institution.name }}</option>
              }
            </select>
          </div>
          <div>
            <label>Course (optional)</label>
            <select class="form-control" [(ngModel)]="createForm.courseId" [disabled]="!createForm.institutionId">
              <option [ngValue]="null">None</option>
              @for (course of courses; track course.id) {
                <option [ngValue]="course.id">{{ course.name }}</option>
              }
            </select>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeCreateModal()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="createUser()" [disabled]="saving">
          <i class="fas fa-save"></i> {{ saving ? 'Saving…' : 'Create User' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Contact Modal -->
@if (showContactModal && selectedUser) {
  <div class="modal-overlay" (click)="closeContactModal()">
    <div class="modal-content user-modal" role="dialog" aria-modal="true" aria-labelledby="contact-title"
      (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title" id="contact-title">Edit Contact Details — {{ userName(selectedUser) }}</div>
        <button type="button" class="modal-close" (click)="closeContactModal()">&times;</button>
      </div>
      <div class="modal-body">
        @if (errorMessage) {
          <div class="alert alert-danger">{{ errorMessage }}</div>
        }
        <div class="form-grid">
          <div class="full-width">
            <label>Email Address</label>
            <input type="email" class="form-control" [(ngModel)]="contactForm.emailAddress">
          </div>
          <div>
            <label>Phone Number</label>
            <input type="text" class="form-control" [(ngModel)]="contactForm.phoneNumber">
          </div>
          <div class="full-width">
            <label>Address</label>
            <input type="text" class="form-control" [(ngModel)]="contactForm.address">
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeContactModal()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="saveContact()" [disabled]="saving">
          <i class="fas fa-save"></i> {{ saving ? 'Saving…' : 'Save Contact' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Roles Modal -->
@if (showRolesModal && selectedUser) {
  <div class="modal-overlay" (click)="closeRolesModal()">
    <div class="modal-content user-modal" role="dialog" aria-modal="true" aria-labelledby="roles-title"
      (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title" id="roles-title">Edit Roles — {{ userName(selectedUser) }}</div>
        <button type="button" class="modal-close" (click)="closeRolesModal()">&times;</button>
      </div>
      <div class="modal-body">
        @if (errorMessage) {
          <div class="alert alert-danger">{{ errorMessage }}</div>
        }
        <div class="role-checkboxes">
          @for (role of roles; track role.id) {
            <label class="role-checkbox">
              <input type="checkbox" [checked]="isRoleSelected(role.id)"
                (change)="toggleRole(role.id, $any($event.target).checked)">
              {{ role.name }}
            </label>
          }
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeRolesModal()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="saveRoles()" [disabled]="saving">
          <i class="fas fa-save"></i> {{ saving ? 'Saving…' : 'Save Roles' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Activity Modal -->
@if (showActivityModal && selectedUser) {
  <div class="modal-overlay" (click)="closeActivityModal()">
    <div class="modal-content activity-modal" role="dialog" aria-modal="true" aria-labelledby="activity-title"
      (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title" id="activity-title">Activity Log — {{ userName(selectedUser) }}</div>
        <button type="button" class="modal-close" (click)="closeActivityModal()">&times;</button>
      </div>
      <div class="modal-body">
        @if (activityError) {
          <div class="alert alert-danger">{{ activityError }}</div>
        } @else if (activityLoading) {
          <div class="empty-cell">Loading activity…</div>
        } @else if (activityLogs.length === 0) {
          <div class="empty-cell">No activity records found.</div>
        } @else {
          <table class="table activity-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              @for (log of activityLogs; track log.id) {
                <tr>
                  <td>{{ log.timestamp | date: 'dd MMM yyyy HH:mm' }}</td>
                  <td><span class="badge badge-primary">{{ log.action }}</span></td>
                  <td>{{ getEntityLabel(log) }}</td>
                  <td>
                    @if (getChangeSummary(log.changes); as changes) {
                      <ul class="change-summary">
                        @for (change of changes; track change.field) {
                          <li>
                            <strong>{{ change.field }}</strong>: {{ change.oldValue }} → {{ change.newValue }}
                          </li>
                        }
                        @if (changes.length === 0) {
                          <li class="muted">—</li>
                        }
                      </ul>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeActivityModal()">Close</button>
      </div>
    </div>
  </div>
}
